/**
 * Clientes: la pantalla de quien administra la plataforma.
 *
 * Aquí se da de alta a cada cliente —su organización y la invitación a quien
 * será su propietario—, se fija cuántos periódicos y miembros tiene contratados
 * y, si hace falta, se le suspende.
 *
 * Enseña cifras, nunca contenido. Para llevar la cuenta de un cliente hace
 * falta saber que usa 7 de sus 10 periódicos, no leer lo que publica.
 *
 * Que esta pantalla no aparezca en el menú de los demás es una cortesía; lo que
 * de verdad la protege es que el servidor rechaza cada una de sus peticiones a
 * quien no administra la plataforma.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppShell } from '../components/app-shell.tsx';
import { Button, ErrorMessage, Field, Input, Notice, Panel, Pill } from '../components/ui.tsx';
import { ApiError, type PlatformClient, platformApi } from '../lib/api.ts';

const DEFAULT_SITES = 10;
const DEFAULT_MEMBERS = 5;

const errorText = (cause: unknown, fallback: string) =>
  cause instanceof ApiError ? cause.message : fallback;

export function ClientsPage() {
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['platform', 'clients'],
    queryFn: platformApi.clients,
  });

  const clients = data?.clients ?? [];

  return (
    <AppShell
      title={t('platform.title')}
      description={t('platform.subtitle')}
      actions={
        <div className="w-44">
          <Button onClick={() => setCreating((open) => !open)}>
            <Plus size={16} aria-hidden="true" />
            {t('platform.newClient')}
          </Button>
        </div>
      }
    >
      {notice ? <Notice>{notice}</Notice> : null}
      <ErrorMessage>{error ? errorText(error, t('errors.generic')) : null}</ErrorMessage>

      {creating ? (
        <NewClientForm
          onDone={(message) => {
            setCreating(false);
            setNotice(message);
          }}
          onCancel={() => setCreating(false)}
        />
      ) : null}

      {isLoading ? <p className="text-sm text-[var(--color-muted)]">{t('common.loading')}</p> : null}

      <div className="space-y-4">
        {clients.map((client) => (
          <ClientCard key={client.id} client={client} onNotice={setNotice} />
        ))}
      </div>
    </AppShell>
  );
}

/* -------------------------------------------------------------------------
 * Alta de un cliente
 * ---------------------------------------------------------------------- */

function NewClientForm({ onDone, onCancel }: { onDone: (message: string) => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [maxSites, setMaxSites] = useState(DEFAULT_SITES);
  const [maxMembers, setMaxMembers] = useState(DEFAULT_MEMBERS);

  const create = useMutation({
    mutationFn: () =>
      platformApi.create({ name: name.trim(), ownerEmail: ownerEmail.trim(), maxSites, maxMembers }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['platform', 'clients'] });
      onDone(t('platform.created', { name: name.trim(), email: ownerEmail.trim() }));
    },
  });

  return (
    <Panel className="mb-6 p-6">
      <h2 className="mb-1 font-display text-xl font-semibold">{t('platform.newClient')}</h2>
      <p className="mb-5 max-w-prose text-sm leading-relaxed text-[var(--color-muted)]">
        {t('platform.newClientHelp')}
      </p>

      <ErrorMessage>{create.error ? errorText(create.error, t('errors.generic')) : null}</ErrorMessage>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <div className="grid gap-x-5 sm:grid-cols-2">
          <Field id="cliente-nombre" label={t('platform.fields.name')}>
            <Input
              id="cliente-nombre"
              required
              maxLength={80}
              placeholder={t('platform.fields.namePlaceholder')}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>

          <Field
            id="cliente-correo"
            label={t('platform.fields.ownerEmail')}
            hint={t('platform.fields.ownerEmailHint')}
          >
            <Input
              id="cliente-correo"
              type="email"
              required
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
            />
          </Field>

          <Field id="cliente-sitios" label={t('platform.fields.maxSites')}>
            <Input
              id="cliente-sitios"
              type="number"
              min={1}
              max={500}
              required
              value={maxSites}
              onChange={(e) => setMaxSites(Number(e.target.value))}
            />
          </Field>

          <Field
            id="cliente-miembros"
            label={t('platform.fields.maxMembers')}
            hint={t('platform.fields.maxMembersHint')}
          >
            <Input
              id="cliente-miembros"
              type="number"
              min={1}
              max={200}
              required
              value={maxMembers}
              onChange={(e) => setMaxMembers(Number(e.target.value))}
            />
          </Field>
        </div>

        <div className="mt-2 flex gap-3 sm:w-96">
          <Button
            type="submit"
            loading={create.isPending}
            disabled={name.trim().length < 2 || ownerEmail.trim() === ''}
          >
            {create.isPending ? t('common.loading') : t('platform.createAndInvite')}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </Panel>
  );
}

/* -------------------------------------------------------------------------
 * Un cliente
 * ---------------------------------------------------------------------- */

function ClientCard({ client, onNotice }: { client: PlatformClient; onNotice: (message: string) => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState(false);
  const [maxSites, setMaxSites] = useState(client.limits.maxSites);
  const [maxMembers, setMaxMembers] = useState(client.limits.maxMembers);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['platform', 'clients'] });
  const onError = (cause: unknown) => setError(errorText(cause, t('errors.generic')));

  const saveLimits = useMutation({
    mutationFn: () => platformApi.update(client.id, { maxSites, maxMembers }),
    onSuccess: async () => {
      setEditing(false);
      setError(null);
      await refresh();
    },
    onError,
  });

  const toggleSuspended = useMutation({
    mutationFn: () => platformApi.update(client.id, { suspended: !client.suspended }),
    onSuccess: async () => {
      setError(null);
      await refresh();
    },
    onError,
  });

  const resend = useMutation({
    mutationFn: () => platformApi.resendInvitation(client.id),
    onSuccess: async (result) => {
      onNotice(t('platform.resent', { email: result.email }));
      await refresh();
    },
    onError,
  });

  const owner = client.owners[0];
  const invitation = client.pendingOwnerInvitation;
  // Sin propietario todavía: la cuenta existe pero nadie ha entrado.
  const awaitingOwner = !owner && Boolean(invitation);

  return (
    <Panel className="p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-display text-xl font-semibold">{client.name}</h2>
            {client.suspended ? <Pill tone="danger">{t('platform.status.suspended')}</Pill> : null}
            {!client.suspended && awaitingOwner ? (
              <Pill tone={invitation?.expired ? 'warning' : 'info'}>
                {invitation?.expired ? t('platform.status.invitationExpired') : t('platform.status.invited')}
              </Pill>
            ) : null}
            {!client.suspended && owner ? <Pill tone="success">{t('platform.status.active')}</Pill> : null}
            {client.paused ? <Pill tone="warning">{t('platform.status.paused')}</Pill> : null}
          </div>

          <p className="mt-1 truncate text-sm text-[var(--color-muted)]">
            {owner ? `${owner.name} · ${owner.email}` : (invitation?.email ?? t('platform.noOwner'))}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {awaitingOwner ? (
            <SmallButton onClick={() => resend.mutate()} disabled={resend.isPending}>
              {t('platform.resend')}
            </SmallButton>
          ) : null}

          <SmallButton onClick={() => setEditing((open) => !open)}>{t('platform.editLimits')}</SmallButton>

          <SmallButton
            danger={!client.suspended}
            disabled={toggleSuspended.isPending}
            onClick={() => {
              // Suspender corta el servicio a un cliente: se confirma. Levantar
              // la suspensión no rompe nada y no hace falta preguntar.
              if (client.suspended || window.confirm(t('platform.suspendConfirm', { name: client.name }))) {
                toggleSuspended.mutate();
              }
            }}
          >
            {client.suspended ? t('platform.resume') : t('platform.suspend')}
          </SmallButton>
        </div>
      </div>

      <ErrorMessage>{error}</ErrorMessage>

      <dl className="mt-4 grid gap-4 sm:grid-cols-4">
        <Meter label={t('platform.usage.sites')} used={client.usage.sites} max={client.limits.maxSites} />
        <Meter
          label={t('platform.usage.members')}
          used={client.usage.members + client.usage.pendingInvitations}
          max={client.limits.maxMembers}
        />
        <Stat label={t('platform.usage.publications')} value={String(client.usage.publicationsLast30Days)} />
        <Stat
          label={t('platform.usage.lastPublication')}
          value={
            client.lastPublicationAt
              ? new Date(client.lastPublicationAt).toLocaleDateString('es-DO', { dateStyle: 'medium' })
              : t('platform.usage.never')
          }
        />
      </dl>

      {editing ? (
        <form
          className="mt-5 border-t border-[var(--color-line)] pt-4"
          onSubmit={(event) => {
            event.preventDefault();
            saveLimits.mutate();
          }}
        >
          <div className="grid gap-x-5 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
            <Field id={`sitios-${client.id}`} label={t('platform.fields.maxSites')}>
              <Input
                id={`sitios-${client.id}`}
                type="number"
                min={1}
                max={500}
                value={maxSites}
                onChange={(e) => setMaxSites(Number(e.target.value))}
              />
            </Field>

            <Field id={`miembros-${client.id}`} label={t('platform.fields.maxMembers')}>
              <Input
                id={`miembros-${client.id}`}
                type="number"
                min={1}
                max={200}
                value={maxMembers}
                onChange={(e) => setMaxMembers(Number(e.target.value))}
              />
            </Field>

            <div className="mb-4 w-36">
              <Button type="submit" loading={saveLimits.isPending}>
                {t('common.save')}
              </Button>
            </div>
          </div>

          {/* Bajar el límite por debajo de lo que ya usa no desconecta nada:
              se dice, para que nadie tema romper algo al ajustar un plan. */}
          {maxSites < client.usage.sites ? (
            <p className="text-xs leading-relaxed text-[var(--color-warning)]">
              {t('platform.belowUsage', { used: client.usage.sites })}
            </p>
          ) : null}
        </form>
      ) : null}
    </Panel>
  );
}

/** Uso frente a límite, con una barra: se ve de un vistazo quién está al tope. */
function Meter({ label, used, max }: { label: string; used: number; max: number }) {
  const ratio = max > 0 ? Math.min(used / max, 1) : 0;
  const full = used >= max;

  return (
    <div>
      <dt className="text-xs text-[var(--color-muted)]">{label}</dt>
      <dd className="mt-1">
        <span className="font-display text-2xl font-semibold tabular-nums">{used}</span>
        <span className="text-sm text-[var(--color-muted)]"> / {max}</span>
        <span
          className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-[var(--color-surface)]"
          role="progressbar"
          aria-valuenow={used}
          aria-valuemin={0}
          aria-valuemax={max}
          aria-label={label}
        >
          <span
            className={`block h-full rounded-full ${full ? 'bg-[var(--color-warning)]' : 'bg-[var(--color-brand)]'}`}
            style={{ width: `${Math.round(ratio * 100)}%` }}
          />
        </span>
      </dd>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-[var(--color-muted)]">{label}</dt>
      <dd className="mt-1 font-display text-2xl font-semibold tabular-nums">{value}</dd>
    </div>
  );
}

function SmallButton({
  children,
  danger,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { danger?: boolean }) {
  return (
    <button
      type="button"
      {...props}
      className={`rounded-lg border px-3 py-1.5 text-sm transition disabled:opacity-50 ${
        danger
          ? 'border-[var(--color-danger)]/30 text-[var(--color-danger)] hover:bg-[var(--color-danger-soft)]'
          : 'border-[var(--color-line-strong)] bg-[var(--color-card)] hover:bg-[var(--color-surface)]'
      }`}
    >
      {children}
    </button>
  );
}
